import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaArrowLeft, FaSave, FaSpinner, FaCreditCard, FaFileAlt, FaCheckCircle } from "react-icons/fa";
import { arClass } from "../../../../utils/useArClass";
import Txt from "../../../Text";
import CustomSelect from "../../../CustomSelect";
import api from "../../../../api";
import { showAlert } from "../../../../redux/alert/alertActions";
import PRICING from "../../../../pages/PRICING.json";
import textTranslator from "../../../../utils/useTextTranslator";
import { BadgeDollarSign, CreditCard, File, Flag, NotebookTabs, Receipt, Save } from "lucide-react";

const Text = ({ children, className, page = "SUBSCRIPTION" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};

const priorityTypes = {
  LOW: {
    label: {
      en: "Low",
      ar: "منخفض",
      fr: "Faible",
    },
    value: "LOW",
    color: "green",
  },
  MEDIUM: {
    label: {
      en: "Medium",
      ar: "متوسط",
      fr: "Moyen",
    },
    value: "MEDIUM",
    color: "yellow",
  },
  HIGH: {
    label: {
      en: "High",
      ar: "مرتفع",
      fr: "Élevé",
    },
    value: "HIGH",
    color: "red",
  },
  CRITICAL: {
    label: {
      en: "Critical",
      ar: "حرج",
      fr: "Critique",
    },
    value: "CRITICAL",
    color: "gray",
  },
};

const requestSubTypes = {
  UPGRADE: {
    label: {
      en: "Upgrade",
      ar: "ترقية",
      fr: "Mise à niveau",
    },
    value: "UPGRADE",
  },
  DOWNGRADE: {
    label: {
      en: "Downgrade",
      ar: "تخفيض",
      fr: "Rétrogradation",
    },
    value: "DOWNGRADE",
  },
  RENEWAL: {
    label: {
      en: "Renewal",
      ar: "تجديد",
      fr: "Renouvellement",
    },
    value: "RENEWAL",
  },
  CANCEL: {
    label: {
      en: "Cancel",
      ar: "إلغاء",
      fr: "Annuler",
    },
    value: "CANCEL",
  },
};

const billingCycles = {
  monthly: {
    label: {
      en: "Monthly",
      ar: "شهري",
      fr: "Mensuel",
    }, 
    value: "monthly",
  },
  yearly: {
    label: {
      en: "Yearly",
      ar: "سنوي",
      fr: "Annuel",
    },
    value: "yearly",
  },
};

const requestedPlans = {
  BASIC: {
    label: {
      en: "Basic",
      ar: "أساسي",
      fr: "Basique",
    },
    value: "BASIC",
  },
  PRO: {
    label: {
      en: "Pro",
      ar: "محترف",
      fr: "Pro",
    },
    value: "PRO",
  },
  ENTERPRISE: {
    label: {
      en: "Enterprise",
      ar: "مؤسسي",
      fr: "Entreprise",
    },
    value: "ENTERPRISE",
  },
};


const CreateSubscriptionRequest = ({ onCancel }) => {
  const darkMode = useSelector((state) => state.settings.darkMode);
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);

  // Get translated plan name from PRICING
  const getTranslatedPlanName = (planKey) => {
    const plan = PRICING[defaultLanguage]?.plans?.[planKey?.toLowerCase()];
    return plan?.name || planKey;
  };

  // Get translated plan description from PRICING
  const getTranslatedPlanDescription = (planKey) => {
    const plan = PRICING[defaultLanguage]?.plans?.[planKey?.toLowerCase()];
    return plan?.description || "";
  };

  // Get translated billing period
  const getTranslatedBillingPeriod = (period) => {
    if (!period) return "";
    const translationKey = `billingPeriod_${period.toLowerCase()}`;
    return PRICING.meta[defaultLanguage]?.billingPeriods?.[translationKey] || period;
  };

  // Get currency from PRICING meta
  const getCurrency = () => {
    return PRICING.meta[defaultLanguage]?.currency || "MAD";
  };

  // Get all available plans from PRICING
  const getAvailablePlans = () => {
    return Object.entries(PRICING[defaultLanguage]?.plans || {})
      .filter(([_, plan]) => plan.isActive)
      .map(([key, plan]) => ({
        key,
        name: plan.name,
        description: plan.description,
      }));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setFetching(true);
        // Fetch current user subscription
        const subscriptionRes = await api.get("users/subscription/").catch(() => ({ data: null }));
        
        // API returns nested structure with plan, nextPayment, lastInvoice
        const planData = subscriptionRes.data?.plan;
        setCurrentSubscription(planData);
        // Set available plans from PRICING data
        setAvailablePlans(getAvailablePlans());

        // Pre-fill form with current subscription data if available
        if (planData) {
          setFormData((prev) => ({
            ...prev,
            currentPlan: planData.planName || "",
            billingCycle: planData.billingPeriod || "monthly",
            price: planData.price || "",
            currency: planData.currency || getCurrency(),
          }));
        }
      } catch (error) {
        console.error("Error fetching subscription data:", error);
      } finally {
        setFetching(false);
      }
    };
    fetchData();
  }, []);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: "MEDIUM",
    requestSubType: "UPGRADE",
    currentPlan: "",
    requestedPlan: "",
    billingCycle: "monthly",
    price: "",
    currency: "MAD",
    additionalNotes: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // If requested plan is changed, auto-fill price and billing cycle from PRICING
    if (name === "requestedPlan" && value) {
      const selectedPlan = PRICING[defaultLanguage]?.plans?.[value.toLowerCase()];
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        price: selectedPlan?.price || "",
        billingCycle: selectedPlan?.billingPeriod || "monthly",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post("it-requests", {
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        requestType: "SUBSCRIPTION_REQUEST",
        subscriptionDetails: {
          requestSubType: formData.requestSubType,
          currentPlan: formData.currentPlan,
          requestedPlan: formData.requestedPlan,
          billingCycle: formData.billingCycle,
          price: parseFloat(formData.price),
          currency: formData.currency,
          additionalNotes: formData.additionalNotes,
        },
      });

      dispatch(
        showAlert({
          type: "success",
          title: textTranslator("Success", "MY_REQUESTS"),
          message: textTranslator("Subscription request created successfully", "MY_REQUESTS"),
        })
      );

      navigate("/my-requests");
    } catch (error) {
      console.error("Error creating request:", error);
      dispatch(
        showAlert({
          type: "error",
          title: "Error",
          message: error?.response?.data?.message || "Failed to create request",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={arClass(`min-h-screen ${
        darkMode ? "bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" : "bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50"
      } transition-colors duration-300 py-8`)}>
      <div className={arClass(" mx-auto px-4 sm:px-6 lg:px-8")}>
        {/* Back Button */}
        <motion.button
          onClick={onCancel}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className={arClass(
            `flex items-center gap-2 px-4 py-2 rounded-lg font-medium mb-8 transition-all ${
              darkMode
                ? "bg-gray-800/50 hover:bg-gray-700/80 text-gray-300 hover:text-white border border-gray-700"
                : "bg-white/50 hover:bg-white text-gray-600 hover:text-gray-900 border border-gray-200 shadow-sm"
            }`
          )}
          whileHover={{ scale: 1.05, x: -5 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaArrowLeft className="w-4 h-4" />
          <Text>Back</Text>
        </motion.button>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className={arClass(
            `rounded-2xl overflow-hidden shadow-2xl border ${
              darkMode
                ? "bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700/50"
                : "bg-white border-gray-200/50"
            }`
          )}
        >
          {/* Header Section */}
          <div className={arClass(`bg-gradient-to-r from-blue-600 to-indigo-600 px-2 py-4 sm:p-6 relative overflow-hidden`)}>
            <motion.div
              className="absolute inset-0 opacity-10"
              animate={{
                background: [
                  "radial-gradient(circle at 20% 50%, #ffffff 0%, transparent 50%)",
                  "radial-gradient(circle at 80% 50%, #ffffff 0%, transparent 50%)",
                  "radial-gradient(circle at 20% 50%, #ffffff 0%, transparent 50%)",
                ],
              }}
              transition={{ duration: 4, repeat: Infinity }}
            />
            <div className={arClass("relative z-10")}>
              <div className={arClass("flex items-center gap-3 mb-2")}>
                <CreditCard className="text-white w-10 h-10 max-sm:w-6 max-sm:h-6" />
                <h1 className="text-3xl max-sm:text-xl font-bold text-white">
                  <Text>Create Subscription Request</Text>
                </h1>
              </div>
              <p className={arClass("text-blue-100 text-sm")}>
                <Text page = "MY_REQUESTS">Manage your subscription and request changes</Text>
              </p>
            </div>
          </div>

          {/* Content Section */}
          <div className={arClass(`px-2 py-4 sm:p-6`)}>
            {fetching ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={arClass("flex flex-col items-center justify-center py-16")}
              >
                <FaSpinner className={`text-5xl animate-spin mb-4 ${darkMode ? "text-blue-400" : "text-blue-600"}`} />
                <p className={arClass(`${darkMode ? "text-gray-400" : "text-gray-600"}`)}>
                  <Text>Loading your subscription data</Text>...
                </p>
              </motion.div>
            ) : (
              <>
                {/* Current Plan Banner */}
                {currentSubscription && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className={arClass(
                      `mb-8 p-6 rounded-xl border-2 ${
                        darkMode
                          ? "bg-gradient-to-r from-green-900/30 to-emerald-900/30 border-green-600/50"
                          : "bg-gradient-to-r from-green-50 to-emerald-50 border-green-200"
                      }`
                    )}
                  >
                    <div className={arClass("flex items-center gap-3")}>
                      <FaCheckCircle className={arClass("text-2xl flex-shrink-0 " + (darkMode ? "text-green-400" : "text-green-600"))} />
                      <div>
                        <p className={arClass(`text-sm font-medium ${darkMode ? "text-green-200" : "text-green-700"}`)}>
                          <Text>Current Plan</Text>
                        </p>
                        <p className={arClass(`text-xl font-bold ${darkMode ? "text-green-100" : "text-green-900"}`)}>
                          {getTranslatedPlanName(currentSubscription?.planName)}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className={arClass("normal-form space-y-6")}>
                  {/* Section 1: Request Details */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className={arClass(
                      `p-6 rounded-xl border-2 ${
                        darkMode
                          ? "bg-gray-800/50 border-gray-700/50"
                          : "bg-gray-50/50 border-gray-200"
                      }`
                    )}
                  >
                    <div className={arClass("flex items-center gap-2 mb-6")}>
                      <File className={arClass("text-lg " + (darkMode ? "text-blue-400" : "text-blue-600"))} />
                      <h2 className={arClass(`text-lg max-sm:text-sm font-bold ${darkMode ? "text-white" : "text-gray-900"}`)}>  
                        <Text>Request Information</Text>
                      </h2>
                    </div>

                    <div className={arClass("space-y-4")}>
                      {/* Title */}
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.25 }}
                      >
                        <label className={arClass(`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                          <Text>Title</Text> <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          placeholder={textTranslator("titlePlaceholder", "SUBSCRIPTION")}
                          required
                          className={arClass(
                            `w-full mt-4 px-4 py-3 rounded-lg border transition-all ${
                              darkMode
                                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                                : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                            }`
                          )}
                        />
                      </motion.div>

                      {/* Description */}
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                      >
                        <label className={arClass(`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                          <Text>Description</Text> <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          placeholder={textTranslator("descriptionPlaceholder", "SUBSCRIPTION")}
                          required
                          rows={4}
                          className={arClass(
                            `w-full mt-4 px-4 py-3 rounded-lg border transition-all resize-none ${
                              darkMode
                                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                                : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                            }`
                          )}
                        />
                      </motion.div>
                    </div>
                  </motion.div>

                  {/* Section 2: Request Type & Priority */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                    className={arClass(
                      `p-6 rounded-xl border-2 ${
                        darkMode
                          ? "bg-gray-800/50 border-gray-700/50"
                          : "bg-gray-50/50 border-gray-200"
                      }`
                    )}
                  >
                    <div className={arClass("flex items-center gap-2 mb-6")}>
                      <Flag className={arClass("text-lg " + (darkMode ? "text-yellow-400" : "text-yellow-600"))} />
<h2 className={arClass(`text-lg max-sm:text-sm font-bold  ${darkMode ? "text-white" : "text-gray-900"}`)}>  
                      <Text>Request Type & Priority</Text>
                    </h2>
                    </div>
                    

                    <div className={arClass("grid grid-cols-1 sm:grid-cols-2 gap-4")}>
                      {/* Priority */}
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 }}
                      >
                        <label className={arClass(`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                          <Text>Priority</Text>
                        </label>
                        <CustomSelect
                            options={Object.entries(priorityTypes).map(
                              ([key, value]) => ({
                                value: key,
                                label: value.label[defaultLanguage] || key,
                              }),
                            )}
                            value={formData.priority || ""}
                            name="priority"
                            onChange={handleInputChange}
                            placeholder={textTranslator("priorityPlaceholder", "SUBSCRIPTION")}
                            allowCreate={false}
                            allowSearch={false}
                            className="max-sm:text-sm mt-4"
                          />
                      </motion.div>

                      {/* Request Type */}
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.45 }}
                      >
                        <label className={arClass(`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                          <Text>Request Type</Text>
                        </label>
                        <CustomSelect
                          name="requestSubType"
                          value={formData.requestSubType}
                          onChange={handleInputChange}
                          options={Object.entries(requestSubTypes).map(
                            ([key, value]) => ({
                              value: key,
                              label: value.label[defaultLanguage] || key,
                            }),
                          )}
                          placeholder={textTranslator(
                            "requestTypePlaceholder",
                            "SUBSCRIPTION",
                          )}
                          allowCreate={false}
                          allowSearch={false}
                          className="max-sm:text-sm mt-4"

                        />
                      </motion.div>
                    </div>
                  </motion.div>

                  {/* Section 3: Plan Selection */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className={arClass(
                      `p-6 rounded-xl border-2 ${
                        darkMode
                          ? "bg-gray-800/50 border-gray-700/50"
                          : "bg-gray-50/50 border-gray-200"
                      }`
                    )}
                  >

                    <div className={arClass("flex items-center gap-2 mb-6")}>
                      <BadgeDollarSign className={arClass("text-lg " + (darkMode ? "text-yellow-400" : "text-yellow-600"))} />
<h2 className={arClass(`text-lg font-bold  ${darkMode ? "text-white" : "text-gray-900"}`)}>
                      <Text>Select Plan</Text>
                    </h2>
                    </div>
                    

                    <div className={arClass("grid grid-cols-1 sm:grid-cols-2 gap-4")}>
                      {/* Current Plan */}
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.55 }}
                      >
                        <label className={arClass(`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                          <Text>Current Plan</Text>
                        </label>
                        <input
                          type="text"
                          name="currentPlan"
                          value={getTranslatedPlanName(formData.currentPlan)}
                          disabled
                          className={arClass(
                            `w-full mt-4 px-4 py-3 rounded-lg border ${
                              darkMode
                                ? "bg-gray-900/50 border-gray-600 text-gray-400 opacity-60"
                                : "bg-gray-100 border-gray-300 text-gray-600 opacity-60"
                            }`
                          )}
                        />
                      </motion.div>

                      {/* Requested Plan */}
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6 }}
                      >
                        <label className={arClass(`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                          <Text>Requested Plan</Text> <span className="text-red-500">*</span>
                        </label>
                        <CustomSelect
                          name="requestedPlan"
                          value={formData.requestedPlan}
                          onChange={handleInputChange}
                          options={availablePlans.map((plan) => ({
                            value: plan.key,
                            label: `${plan.name}`,
                          }))}
                          isMulti={false}
                          isClearable
                          placeholder={textTranslator("selectPlanPlaceholder", "SUBSCRIPTION")}
                          allowCreate={false}
                          allowSearch={false}
                          className="max-sm:text-sm mt-4"
                        />
                      </motion.div>
                    </div>
                  </motion.div>

                  {/* Section 4: Billing Details */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.65 }}
                    className={arClass(
                      `p-6 rounded-xl border-2 ${
                        darkMode
                          ? "bg-gradient-to-r from-purple-900/20 to-pink-900/20 border-purple-600/50"
                          : "bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200"
                      }`
                    )}
                  >
                      <div className={arClass("flex items-center gap-2 mb-6")}>
                        <Receipt className={arClass("text-lg " + (darkMode ? "text-purple-400" : "text-purple-600"))} />
                        <h2 className={arClass(`text-lg max-sm:text-sm font-bold  ${darkMode ? "text-white" : "text-gray-900"}`)}>  
                          <Text>Billing Information</Text>
                        </h2>
                      </div>

                    <div className={arClass("grid grid-cols-1 sm:grid-cols-3 gap-4 items-center")}>
                      {/* Billing Cycle */}
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.7 }}
                      >
                        <label className={arClass(`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                          <Text>Billing Cycle</Text>
                        </label>
                        <CustomSelect
                          name="billingCycle"
                          value={formData.billingCycle}
                          onChange={handleInputChange}
                          options={Object.entries(billingCycles).map(
                            ([key, value]) => ({
                              value: key,
                              label: value.label[defaultLanguage] || key,
                            }),
                          )}
                          placeholder={textTranslator(
                            "billingCyclePlaceholder",
                            "SUBSCRIPTION",
                          )}
                          allowCreate={false}
                          allowSearch={false}
                          className="max-sm:text-sm mt-4"
                        />
                      </motion.div>

                      {/* Price */}
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.75 }}
                      >
                        <label className={arClass(`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                          <Text>Price</Text>
                        </label>
                        <div className={arClass(
                          `w-full mt-4 px-4 py-2 rounded-lg border flex items-center ${
                            darkMode
                              ? "bg-gray-900/50 border-gray-600"
                              : "bg-gray-100 border-gray-300"
                          }`
                        )}>
                          <span className={arClass(`text-lg max-sm:text-sm font-bold ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                            {formData.price || "0.00"}
                          </span>
                        </div>
                      </motion.div>

                      {/* Currency */}
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.8 }}
                      >
                        <label className={arClass(`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                          <Text>Currency</Text>
                        </label>
                        <div className={arClass(
                          `w-full mt-4 px-4 py-2 rounded-lg border flex items-center ${
                            darkMode
                              ? "bg-gray-900/50 border-gray-600"
                              : "bg-gray-100 border-gray-300"
                          }`
                        )}>
                          <span className={arClass(`text-lg max-sm:text-sm font-bold ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                            {getCurrency()}
                          </span>
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>

                  {/* Section 5: Additional Notes */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.85 }}
                    className={arClass(
                      `p-6 rounded-xl border-2 ${
                        darkMode
                          ? "bg-gray-800/50 border-gray-700/50"
                          : "bg-gray-50/50 border-gray-200"
                      }`
                    )}
                  >
                    <label className={arClass(`text-sm font-semibold flex items-center gap-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                        <NotebookTabs className={arClass("text-base max-sm:text-sm " + (darkMode ? "text-gray-400" : "text-gray-600"))} />
                       <Text>Additional Notes</Text>
                    </label>
                    <textarea
                      name="additionalNotes"
                      value={formData.additionalNotes}
                      onChange={handleInputChange}
                      placeholder={textTranslator("additionalNotesPlaceholder", "SUBSCRIPTION")}
                      rows={3}
                      className={arClass(
                        `w-full mt-4 px-4 py-3 rounded-lg border transition-all resize-none ${
                          darkMode
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                        }`
                      )}
                    />
                  </motion.div>

                  {/* Submit Button */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9 }}
                    className={arClass("flex gap-4 pt-4")}
                  >
                    <motion.button
                      type="submit"
                      disabled={loading}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={arClass(
                        `flex-1 flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-bold transition-all ${
                          loading
                            ? "opacity-50 cursor-not-allowed"
                            : darkMode
                            ? "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-600/50"
                            : "bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/50"
                        }`
                      )}
                    >
                      <Save className="w-5 h-5" />
                      <Text>{loading ? "Creating Request..." : "Create Request"}</Text>
                    </motion.button>
                  </motion.div>
                </form>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CreateSubscriptionRequest;
